import React, { useEffect } from 'react';
import { fetchFooterLinks, footerLinksSelector } from 'Douleutaras/redux/data/footerLinks/footerLinksSlice';
import { useAppDispatch, useAppSelector } from 'Douleutaras/redux/hooks';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_CODES } from 'Douleutaras/constants/system';
import NewsletterSubscribeForm from 'Douleutaras/layout/partials/NewsletterSubscribeForm';
import { COUNTRY_CONFIG, LANGUAGE_CONFIG, SITE_COUNTRY_CODE, SITE_ID } from 'Douleutaras/constants/localization';
import { getCountryCode, getCurrentLanguage, useLocalization } from 'Douleutaras/hooks/localization';
import LanguageSwitcher from 'Douleutaras/layout/partials/LanguageSwitcher';
import { FooterAccordion } from 'Douleutaras/layout/partials/FooterAccordion/FooterAccordion';
import { GetServerSideProps } from 'Douleutaras/types/ssr';

import './DefaultFooter.scss';

// Renders on every full-layout page via DefaultFullLayout, not a routed view, so it
// can't opt into a leaf route's loader — wired instead on the root layout route in
// dataRoutes.tsx. fetchFooterLinks caches per language/country, so this is a no-op
// after the first request for a given locale.
export const getServerSideProps: GetServerSideProps = async ({ store }) => {
  await store.dispatch(fetchFooterLinks({
    language: getCurrentLanguage(),
    countryCode: getCountryCode()
  }));
};

enum FooterColumns {
  COMPANY = 'company',
  CUSTOMERS = 'customers',
  PROFESSIONALS = 'professionals',
  COUNTRY = 'country'
}

export const getFooterLinks = (footerLinks, column: FooterColumns, Target?) =>
  footerLinks?.links?.filter(link => link.column === column)
  .map(link =>
    <div className="footer-nav__item" key={link.url}>
      <a href={link.url} className="footer-nav__link" target={Target ? '_blank' : '_self'}>
        {link.screen_name}
      </a>
    </div>
);

export default () => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const { countryCode } = useLocalization();

  const footerLinks = useAppSelector(footerLinksSelector);

  useEffect(() => {
    if (footerLinks?.links) {
      // Already populated — either hydrated from SSR or a prior mount. Skip the refetch.
      return;
    }
    dispatch(fetchFooterLinks({ language: i18n.language as LANGUAGE_CODES, countryCode }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const MOBILE_APP_LINKS = {
    GOOGLE_APP_LINK: {
      [SITE_COUNTRY_CODE.GR]: 'https://play.google.com/store/apps/details?id=com.yourhero.mobile.customers',
      [SITE_COUNTRY_CODE.CY]: 'https://play.google.com/store/apps/details?id=com.yourhero.mobile.customers',
      [SITE_COUNTRY_CODE.IE]: 'https://play.google.com/store/apps/details?id=com.yourhero.mobile.customers.irish'
    },
    GOOGLE_APP_IMG: {
      [LANGUAGE_CODES.EL]: '/douleutaras/img/douleutaras/google-play-el.svg',
      [LANGUAGE_CODES.EL_CY]: '/douleutaras/img/douleutaras/google-play-el.svg',
      [LANGUAGE_CODES.EN]: '/douleutaras/img/douleutaras/google-play-en.svg',
      [LANGUAGE_CODES.EN_IE]: '/douleutaras/img/douleutaras/google-play-en.svg'
    },
    APP_STORE_LINK: {
      [SITE_COUNTRY_CODE.GR]: 'https://apps.apple.com/us/app/douleutaras-hire-local-pros/id1587680274',
      [SITE_COUNTRY_CODE.CY]: 'https://apps.apple.com/us/app/douleutaras-hire-local-pros/id1587680274',
      [SITE_COUNTRY_CODE.IE]: 'https://apps.apple.com/us/app/yourpro-find-a-professional/id1614231111'
    },
    APP_STORE_IMG: {
      [LANGUAGE_CODES.EL]: '/douleutaras/img/douleutaras/app-store-el.svg',
      [LANGUAGE_CODES.EL_CY]: '/douleutaras/img/douleutaras/app-store-el.svg',
      [LANGUAGE_CODES.EN]: '/douleutaras/img/douleutaras/app-store-en.svg',
      [LANGUAGE_CODES.EN_IE]: '/douleutaras/img/douleutaras/app-store-en.svg'
    }
  };

  return (
    <footer styleName="default-footer">
      <div className="default-footer-top">
        <div className="custom-container">
          <div className="default-footer-top-inner">
            <div className="footer-nav">
              <FooterAccordion title={t('Company')}>
                { getFooterLinks(footerLinks, FooterColumns.COMPANY) }
              </FooterAccordion>

              <FooterAccordion title={t('Customers')}>
                { getFooterLinks(footerLinks, FooterColumns.CUSTOMERS) }
              </FooterAccordion>

              <FooterAccordion title={t('Professionals')}>
                { getFooterLinks(footerLinks, FooterColumns.PROFESSIONALS) }
              </FooterAccordion>

              <FooterAccordion title={t('T.LAYOUT.COUNTRY-REGION')}>
                { getFooterLinks(footerLinks, FooterColumns.COUNTRY, true) }
              </FooterAccordion>
            </div>
            <div className="footer-features">
              <NewsletterSubscribeForm />

              <div className="footer-app">
                <h6 className="footer-app__title">
                  {t('Download our App')}
                </h6>
                <nav className="footer-app__list">
                    <a className="footer-app__link" href={MOBILE_APP_LINKS.GOOGLE_APP_LINK[countryCode]} target="_blank">
                      <img
                        className="footer-app__img"
                        src={`${process.env.ASSETS_CDN_PATH}${MOBILE_APP_LINKS.GOOGLE_APP_IMG[i18n.language]}`}
                        alt="Google play"
                        width={108}
                        height={32}
                      />
                    </a>
                    <a className="footer-app__link" href={MOBILE_APP_LINKS.APP_STORE_LINK[countryCode]} target="_blank">
                      <img
                        className="footer-app__img"
                        src={`${process.env.ASSETS_CDN_PATH}${MOBILE_APP_LINKS.APP_STORE_IMG[i18n.language]}`}
                        alt="App store"
                        width={97}
                        height={32}
                      />
                    </a>
                </nav>
              </div>
             <LanguageSwitcher isDropdownSwitcher />
            </div>
          </div>
        </div>
      </div>
      <div className="default-footer-bottom">
        <div className="custom-container">
          <div className="default-footer-bottom-inner">
            <div className="footer-bottom-left">
              <article className="default-footer-logo">
                <a href="/" className="default-footer-logo__link">
                    <span className="default-footer-logo__img">
                      <img className="default-header-logo__img"
                         src={`${process.env.ASSETS_CDN_PATH}/douleutaras/img/vendor/v1/dashboard/logo/${SITE_ID[countryCode]}-logo.svg`}
                         alt={COUNTRY_CONFIG.SITE_NAME[countryCode]}
                    />
                    </span>
                </a>
              </article>
              <article className="default-footer-copyright">
                <p className="default-footer-copyright__text">
                  @ 2015 - {new Date().getFullYear()} {COUNTRY_CONFIG.SITE_URL_LABEL[countryCode]} - All rights reserved
                </p>
              </article>
              <article className="footer-disclaimer">
                <a href={LANGUAGE_CONFIG.LINKS.TERMS_GENERAL[i18n.language]} className="footer-disclaimer__link">
                  {t('Terms and conditions')}
                </a>
                <span>-</span>
                <a href={LANGUAGE_CONFIG.LINKS.PRIVACY_POLICY[i18n.language]} className="footer-disclaimer__link">
                  {t('Privacy Policy')}
                </a>
                <span>-</span>
                <a href={LANGUAGE_CONFIG.LINKS.ANTI_CORRUPTION_POLICY[i18n.language]} className="footer-disclaimer__link">
                  {t('Anti-Corruption Policy')}
                </a>
              </article>
            </div>
            {(countryCode === SITE_COUNTRY_CODE.GR || countryCode === SITE_COUNTRY_CODE.CY) && (
              <div className="footer-bottom-right">
                <article className="footer-partners">
                  <div className="footer-partners-item">
                    <img
                      className="footer-partners-item__img"
                      alt="Skroutz"
                      src={`${process.env.ASSETS_CDN_PATH}/douleutaras/img/douleutaras/Skroutz.svg`}
                    />
                  </div>
                  <div className="footer-partners-item">
                    <img
                      className="footer-partners-item__img"
                      alt="IKEA"
                      src={`${process.env.ASSETS_CDN_PATH}/douleutaras/img/douleutaras/IKEA.svg`}
                    />
                  </div>
                  <div className="footer-partners-item">
                    <img
                      className="footer-partners-item__img"
                      alt="Elpedison"
                      src={`${process.env.ASSETS_CDN_PATH}/douleutaras/img/douleutaras/Elpedison.svg`}
                    />
                  </div>
                  {countryCode === SITE_COUNTRY_CODE.GR && (
                    <div className="footer-partners-item">
                      <img
                        className="footer-partners-item__img"
                        alt="Psifiakos"
                        src={`${process.env.ASSETS_CDN_PATH}/douleutaras/img/douleutaras/Psifiakos.png`}
                      />
                    </div>
                  )}
                </article>
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};
